import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface ComparisonItem {
  id: string;
  name: string;
  rating: number;
  price: number;
  image: string;
  badge: string;
  feature: string;
  pros: string[];
  cons: string[];
}

const EXCLUDED_TERMS = ['%charger%', '%capture card%', '%vr headset%'];

export default function Comparison() {
  const [items, setItems] = useState<ComparisonItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchComparison = async () => {
      try {
        let query = supabase
          .from('products')
          .select('id,name,rating,price,image,badge')
          .or('name.ilike.%headphone%,name.ilike.%earbud%,name.ilike.%earphone%');
        for (const term of EXCLUDED_TERMS) {
          query = query.not('name', 'ilike', term);
        }
        const { data: products, error: prodErr } = await query.order('rating', { ascending: false }).limit(4);
        if (cancelled) return;
        if (prodErr || !products || products.length === 0) {
          setError(true);
          setLoading(false);
          return;
        }
        const ids = products.map(p => p.id);
        const [{ data: highlights }, { data: pros }, { data: cons }] = await Promise.all([
          supabase.from('product_highlights').select('product_id, text').in('product_id', ids).eq('sort_order', 1),
          supabase.from('product_pros').select('product_id, text').in('product_id', ids).order('sort_order').limit(3 * ids.length),
          supabase.from('product_cons').select('product_id, text').in('product_id', ids).order('sort_order').limit(2 * ids.length),
        ]);
        if (cancelled) return;

        const withDetails = products.map((p) => ({
          ...p,
          feature: highlights?.find(h => h.product_id === p.id)?.text || '',
          pros: (pros || []).filter(pr => pr.product_id === p.id).slice(0, 3).map(pr => pr.text),
          cons: (cons || []).filter(c => c.product_id === p.id).slice(0, 2).map(c => c.text),
        }));
        setItems(withDetails);
      } catch {
        if (cancelled) return;
        setError(true);
      }
      setLoading(false);
    };
    fetchComparison();
    return () => { cancelled = true; };
  }, []);

  if (loading || items.length === 0 || error) return null;

  return (
    <section id="compare" className="py-20 md:py-28 bg-ink-50/60 reveal">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-semibold uppercase tracking-widest text-cta-600">Head-to-Head</span>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-ink-900 tracking-tight">
            Wireless Headphones Compared
          </h2>
          <p className="mt-3 text-ink-600 leading-relaxed">
            Our top-rated wireless headphones and earbuds, ranked side by side.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-ink-100 shadow-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="bg-ink-50/70 border-b border-ink-100">
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-6 py-4">Product</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-6 py-4">Rating</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-6 py-4">Price</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-6 py-4">Key Feature</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-ink-500 px-6 py-4">Pros / Cons</th>
                  <th className="px-6 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, i) => (
                  <tr key={r.id} className={`border-b border-ink-100 last:border-b-0 hover:bg-ink-50/50 transition ${i === 0 ? 'bg-cta-50/40' : ''}`}>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-xl bg-ink-100 overflow-hidden flex-shrink-0">
                          <img src={r.image} alt={r.name} className="w-full h-full object-contain p-1" />
                        </div>
                        <div>
                          <p className="font-semibold text-ink-900 text-sm max-w-[220px] line-clamp-2">{r.name}</p>
                          {(i === 0 || r.badge) && (
                            <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase text-cta-700 bg-cta-100 px-1.5 py-0.5 rounded whitespace-nowrap">
                              <span className="w-3 h-3 flex items-center justify-center">
                                <i className="ri-medal-line"></i>
                              </span>
                              {r.badge || 'Top Pick'}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-1.5">
                        <i className="ri-star-fill text-amber-500"></i>
                        <span className="font-semibold text-ink-900 text-sm">{r.rating}</span>
                      </div>
                    </td>
                    <td className="px-6 py-5 font-display font-bold text-ink-900 whitespace-nowrap">${r.price}</td>
                    <td className="px-6 py-5 text-sm text-ink-700 max-w-[240px]">{r.feature}</td>
                    <td className="px-6 py-5 max-w-[280px]">
                      <ul className="space-y-1">
                        {r.pros.map((p) => (
                          <li key={p} className="flex items-start gap-1.5 text-xs text-ink-700">
                            <i className="ri-check-line text-emerald-500 mt-0.5"></i> {p}
                          </li>
                        ))}
                        {r.cons.map((c) => (
                          <li key={c} className="flex items-start gap-1.5 text-xs text-ink-500">
                            <i className="ri-close-line text-rose-500 mt-0.5"></i> {c}
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="px-6 py-5">
                      <Link
                        to={`/product/${r.id}`}
                        className="cta-btn h-9 px-4 rounded-lg bg-cta-500 hover:bg-cta-600 text-white text-xs font-semibold whitespace-nowrap transition inline-flex items-center gap-1.5"
                      >
                        Check Price
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-arrow-right-line"></i>
                        </span>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
